import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Navigate } from "@ngxs/router-plugin";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EMPTY, finalize, tap, timer } from "rxjs";
import { environment } from "../../../environments/environment";
import {
	BaseState,
	CollectionState,
	ItemState,
	initialCollectionState,
	initialItemState,
	loadingStart,
	loadingStop,
	setItem,
	setItems,
} from "../../model/custom/Utils";
import {
	CompanyContactModel,
	CompanyModel,
	InductionAttemptModel,
	SiteModel,
	SiteSignInContactModel,
} from "../../model/db-schema";
import { UtilsService } from "../../shared/service/utils.service";
import { StoredAuthRemoveAction } from "../../shared/state/stored-auth.actions";
import {
	BackToStart,
	ConfigureAsKiosk,
	GetCompanyContactsToNotify,
	GetContactAttempts,
	GetPortalDetails,
	Print,
	RegisterContact,
	ResumeInduction,
	Search,
	SearchSignedInContacts,
	SetCompanyContactToNotify,
	SetSignInFlow,
	SetSignInType,
	SetSignOutFlow,
	SignIn,
	SignOut,
	StartInduction,
} from "./sign-in-kiosk.actions";
export enum SignInType {
	Visitor = "Visitor",
	Contractor = "Contractor",
	Employee = "Employee",
	Other = "Other",
}

export const SignInTypes = [
	SignInType.Employee,
	SignInType.Visitor,
	SignInType.Contractor,
	SignInType.Other,
] as const;
export type ContactSignInType = SignInType;

export enum SignInPages {
	InitialTextFields,
	Induction,
	Signature,
	CapturePhoto,
	WhomVisitingList,
	Finish,
	CustomForm,
}

export enum SignOutPages {
	InitialTextFields,
	CapturePhoto,
	Finish,
}

export const EmployeeSignInFlow: SignInPages[] = [
	SignInPages.InitialTextFields,
	SignInPages.CapturePhoto,
	SignInPages.Finish,
];

export const DefaultSignInFlow: SignInPages[] = [
	SignInPages.InitialTextFields,
	SignInPages.Induction,
	SignInPages.WhomVisitingList,
	SignInPages.Signature,
	SignInPages.CapturePhoto,
	SignInPages.Finish,
];

export const DefaultSignOutFlow: SignOutPages[] = [
	SignOutPages.InitialTextFields,
	SignOutPages.CapturePhoto,
	SignOutPages.Finish,
];

export interface IPortalInduction {
	ID: string;
	Title: string;
}
export type SignInSettingsType =
	| {
			SignInUserTypesSettings: Partial<
				Record<ContactSignInType, { Inductions: IPortalInduction[] }>
			>[];
			Other:
				| {
						Visible: boolean;
						SignInImage: boolean;
						SignOutImage: boolean;
						FormItems: any;
						FormDescription?: string;
				  }
				| undefined;
			Visitor:
				| {
						Visible: boolean;
						SignInImage: boolean;
						SignOutImage: boolean;
						FormItems: any;
						FormDescription?: string;
				  }
				| undefined;
			Employee:
				| {
						Visible: boolean;
						SignInImage: boolean;
						SignOutImage: boolean;
						FormItems: any;
						FormDescription?: string;
				  }
				| undefined;
			Contractor:
				| {
						Visible: boolean;
						SignInImage: boolean;
						SignOutImage: boolean;
						FormItems?: any;
						FormDescription?: string;
				  }
				| undefined;
	  }
	| undefined;

export interface ISignInPortal extends SiteModel {
	SignInSettings: SignInSettingsType;
	Image: CompanyModel["Image"];
	CompanyName: CompanyModel["Name"];
	OfficePhoneNumber: CompanyModel["OfficePhoneNumber"];
}

export type SignInUserTypesSettings =
	ISignInPortal["SignInSettings"]["SignInUserTypesSettings"];
export type SignInContact = CompanyContactModel & { SiteSignIn?: SiteSignInContactModel };
export type SignInKioskCompanyContactsToNotify = {
	ID: CompanyContactModel["ID"];
	FirstName: CompanyContactModel["FirstName"];
	LastName: CompanyContactModel["LastName"];
	Image: CompanyContactModel["Image"];
	ImageRotate: CompanyContactModel["ImageRotate"];
};

export class SignInKioskStateModel {
	public settings: { slug: string; device?: string; isInKioskMode?: boolean };
	public contact: ItemState<
		CompanyContactModel & { SiteSignIn?: SiteSignInContactModel }
	>;
	public contactSignInType: ContactSignInType | null;
	public selectedCompanyContactToNotify: SignInKioskCompanyContactsToNotify | null;
	public attempts: CollectionState<InductionAttemptModel>;
	public companyContactsToNotify: CollectionState<SignInKioskCompanyContactsToNotify>;
	public portal: ItemState<ISignInPortal>;
	public signInFlow: SignInPages[];
	public signOutFlow: SignOutPages[];
	public isInKioskMode: boolean;
}

const defaults: SignInKioskStateModel = {
	settings: { slug: "" },
	contact: { ...initialItemState },
	contactSignInType: null,
	selectedCompanyContactToNotify: null,
	portal: { ...initialItemState },
	attempts: { ...initialCollectionState },
	companyContactsToNotify: { ...initialCollectionState },
	signInFlow: [],
	signOutFlow: DefaultSignOutFlow,
	isInKioskMode: false,
};

@State<SignInKioskStateModel>({ name: "signInKiosk", defaults })
@Injectable()
export class SignInKioskState extends BaseState<SignInKioskStateModel> {
	private http = inject(HttpClient);
	private utils = inject(UtilsService);

	constructor() {
		super();
	}

	@Selector()
	static SignInKioskSlug(state: SignInKioskStateModel): string {
		return state.settings.slug;
	}

	@Selector()
	static Portal(state: SignInKioskStateModel): ISignInPortal {
		return state.portal.item;
	}
	@Selector()
	static SignInSettings(state: SignInKioskStateModel): SignInSettingsType {
		return state.portal.item?.SignInSettings;
	}

	@Selector()
	static SignInTypes(state: SignInKioskStateModel): ContactSignInType[] {
		return SignInTypes.map((i) => i);
	}

	@Selector()
	static IsSignedIn(state: SignInKioskStateModel): boolean {
		return !!state.contact?.item.SiteSignIn?.ID;
	}

	@Selector()
	static Contact(
		state: SignInKioskStateModel,
	): ItemState<CompanyContactModel & { SiteSignIn?: SiteSignInContactModel }> {
		return state.contact;
	}
	@Selector()
	static ContactAttempts(state: SignInKioskStateModel): InductionAttemptModel[] {
		return state.attempts?.items ?? [];
	}
	@Selector()
	static CompanyContactsToNotify(
		state: SignInKioskStateModel,
	): SignInKioskCompanyContactsToNotify[] {
		return state.companyContactsToNotify.items;
	}

	@Selector()
	static AttemptsLoading(state: SignInKioskStateModel): boolean {
		return !state.attempts.isFinishedLoading;
	}

	@Selector()
	static SignInType(state: SignInKioskStateModel): ContactSignInType {
		return state.contactSignInType;
	}

	@Selector()
	static SignInFlow(state: SignInKioskStateModel): SignInPages[] {
		return state.signInFlow;
	}

	@Selector()
	static SignOutFlow(state: SignInKioskStateModel): SignOutPages[] {
		return state.signOutFlow;
	}

	@Selector([
		SignInKioskState.PortalInductionsForType,
		SignInKioskState.ContactAttempts,
	])
	static IncompleteInductions(
		portalInductions: IPortalInduction[],
		attempts: InductionAttemptModel[],
	): IPortalInduction[] {
		const inductionIDs = portalInductions.map((i) => i.ID);
		const completedInductionIDs = attempts
			.filter(
				(a) =>
					inductionIDs.includes(a.ParentInductionID) &&
					a.Status === "Successful",
			)
			.map((a) => a.ParentInductionID);
		return portalInductions.filter((i) => !completedInductionIDs.includes(i.ID));
	}

	@Selector([SignInKioskState.Portal, SignInKioskState.SignInType])
	static PortalInductionsForType(
		portal: ISignInPortal,
		signInType: SignInType,
	): IPortalInduction[] {
		return Array.isArray(portal?.SignInSettings?.SignInUserTypesSettings)
			? portal.SignInSettings.SignInUserTypesSettings.filter(
					(i) => signInType in i,
				).flatMap((i) => i[signInType].Inductions)
			: [];
	}

	@Selector([SignInKioskState.IncompleteInductions])
	static ShouldCompleteInductions(incompleteInductions: IPortalInduction[]): boolean {
		return incompleteInductions.length > 0;
	}

	@Action(ConfigureAsKiosk)
	public ConfigureAsKiosk(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: { slug: string; device?: string; redirect?: boolean } },
	) {
		const state = ctx.getState();
		const settings = {
			slug: payload.slug,
			device: payload.device,
			isInKioskMode: payload.redirect,
		};
		ctx.setState({ ...state, settings });
		if (payload.redirect) {
			ctx.dispatch([
				new StoredAuthRemoveAction(),
				new Navigate(["sign-in-kiosk"]),
				new GetPortalDetails(),
			]);
		} else {
			ctx.dispatch([new GetPortalDetails()]);
		}
	}

	@Action(BackToStart)
	public BackToStart(ctx: StateContext<SignInKioskStateModel>) {
		const { contactSignInType, contact, attempts, selectedCompanyContactToNotify } =
			ctx.getState();
		setItem(
			ctx,
			{ selectedCompanyContactToNotify },
			defaults.selectedCompanyContactToNotify,
		);
		setItem(ctx, { contact }, null);
		setItems(ctx, { attempts }, []);

		timer(5 * 1000)
			.pipe(
				tap(() => {
					ctx.dispatch([new Navigate(["sign-in-kiosk"])]);
					setItem(ctx, { contactSignInType }, defaults.contactSignInType);
				}),
			)
			.subscribe();
	}

	@Action(SetSignInType)
	public SetSignInType(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: ContactSignInType },
	) {
		const { patchState } = ctx;
		patchState({ contactSignInType: payload });
	}
	@Action(SetCompanyContactToNotify)
	public SetCompanyContactToNotify(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: SignInKioskCompanyContactsToNotify },
	) {
		const { patchState } = ctx;
		patchState({ selectedCompanyContactToNotify: payload });
	}

	@Action(Search)
	public Search(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: { mobile: string } },
	) {
		const { contact, settings } = ctx.getState();
		const params = this.utils.convertObjToQueryString(settings);

		return this.handleServiceCall(
			ctx,
			{ contact },
			this.http.post(
				`${environment.apiURL}/sign-in/kiosk/search${params}`,
				payload,
			),
		);
	}
	@Action(SearchSignedInContacts)
	public SearchSignedInContacts(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: { mobile: string } },
	) {
		const { contact, settings } = ctx.getState();
		const params = this.utils.convertObjToQueryString(settings);

		return this.handleServiceCall(
			ctx,
			{ contact },
			this.http.post(
				`${environment.apiURL}/sign-in/kiosk/search-signed-in${params}`,
				payload,
			),
		);
	}

	@Action(RegisterContact)
	public RegisterContact(
		ctx: StateContext<SignInKioskStateModel>,
		{
			payload,
		}: {
			payload: {
				FirstName: string;
				LastName: string;
				PrimaryEmail: string;
				CompanyName: string;
				PrimaryContactNumber: string;
			};
		},
	) {
		const { contact, settings } = ctx.getState();
		const params = this.utils.convertObjToQueryString(settings);

		return this.handleServiceCall(
			ctx,
			{ contact },
			this.http.post(`${environment.apiURL}/sign-in/kiosk/register${params}`, payload),
		);
	}

	@Action(SignIn)
	public SignIn(
		ctx: StateContext<SignInKioskStateModel>,
		{
			payload,
		}: {
			payload: {
				imageURL: string;
				signature?: string;
				formItems?: any;
			};
		},
	) {
		const { contact, contactSignInType, settings, selectedCompanyContactToNotify } =
			ctx.getState();
		const data = {
			contactID: contact.item.ID,
			imageURL: payload.imageURL,
			contactSignInType: contactSignInType,
			companyContactsToNotifyID: selectedCompanyContactToNotify?.ID,
			signature: payload.signature,
			formItems: payload.formItems,
		};
		const params = this.utils.convertObjToQueryString(settings);

		return this.handleServiceCall(
			ctx,
			{ contact },
			this.http.post(`${environment.apiURL}/sign-in/kiosk/sign-in${params}`, data),
			() => {},
		);
	}

	@Action(SignOut)
	public SignOut(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: { imageURL: string } },
	) {
		const { contact, settings } = ctx.getState();

		const data = {
			contactID: contact.item.ID,
			imageURL: payload.imageURL,
			signInID: contact.item.SiteSignIn?.ID,
		};
		const params = this.utils.convertObjToQueryString(settings);

		return this.handleServiceCall(
			ctx,
			{ contact },
			this.http.post(`${environment.apiURL}/sign-in/kiosk/sign-out${params}`, data),
			() => {},
		);
	}

	@Action(ResumeInduction)
	public ResumeInduction(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: { referenceID: string; parentInductionID: string } },
	) {
		const { referenceID, parentInductionID } = payload;
		const origin = "sign-in-out";
		const params = `${referenceID}/${parentInductionID}/${origin}`;

		ctx.dispatch([new Navigate([`attempt/induction/${params}`])]);
	}

	@Action(StartInduction)
	public StartInduction(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: { inductionID: string } },
	) {
		const { attempts, contact, settings } = ctx.getState();

		const contactID = contact.item?.ID;
		if (!contactID) {
			return EMPTY;
		}

		const data = { contactID, inductionID: payload.inductionID };
		const params = this.utils.convertObjToQueryString(settings);

		return this.handleServiceCall(
			ctx,
			{ attempts },
			this.http.post(
				`${environment.apiURL}/sign-in/kiosk/contact-induction-start${params}`,
				data,
			),
			(res: InductionAttemptModel) => {
				setItems(ctx, { attempts }, [...attempts.items, res]);
				const {
					ReferenceID: referenceID,
					ParentInductionID: parentInductionID,
				} = res;
				const origin = "sign-in-out";
				const params = `${referenceID}/${parentInductionID}/${origin}`;

				ctx.dispatch([new Navigate([`attempt/induction/${params}`])]);
			},
		);
	}

	@Action(GetPortalDetails)
	public GetPortalDetails(ctx: StateContext<SignInKioskStateModel>) {
		const { portal, settings } = ctx.getState();
		const params = this.utils.convertObjToQueryString(settings);

		return this.handleServiceCall(
			ctx,
			{ portal },
			this.http.get(`${environment.apiURL}/sign-in/kiosk/details/${params}`),
		);
	}
	@Action(GetContactAttempts)
	public GetContactAttempts(ctx: StateContext<SignInKioskStateModel>) {
		const { attempts, settings, contact } = ctx.getState();

		const params = this.utils.convertObjToQueryString({
			...settings,
			contactID: contact.item.ID,
		});
		return this.handleCollectionCall(
			ctx,
			{ attempts },
			this.http.get<InductionAttemptModel[]>(
				`${environment.apiURL}/sign-in/kiosk/contact-attempts/${params}`,
			),
		);
	}

	@Action(GetCompanyContactsToNotify)
	public GetCompanyContactsToNotify(ctx: StateContext<SignInKioskStateModel>) {
		const { companyContactsToNotify, settings } = ctx.getState();

		const params = this.utils.convertObjToQueryString({ ...settings });
		return this.handleCollectionCall(
			ctx,
			{ companyContactsToNotify },
			this.http.get<SignInKioskCompanyContactsToNotify[]>(
				`${environment.apiURL}/sign-in/kiosk/company-contact-to-notify/${params}`,
			),
		);
	}
	@Action(Print)
	public Print(ctx: StateContext<SignInKioskStateModel>) {
		const { contact, settings } = ctx.getState();

		const params = this.utils.convertObjToQueryString(settings);
		return this.handleServiceCall(
			ctx,
			{ contact },
			this.http.post(`${environment.apiURL}/sign-in/kiosk/print/${params}`, {
				signInID: contact.item.SiteSignIn?.ID,
			}),
			() => {},
		);
	}

	@Action(SetSignInFlow)
	public SetSignInFlow(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: SignInPages[] },
	) {
		const { patchState } = ctx;
		patchState({ signInFlow: payload });
	}
	@Action(SetSignOutFlow)
	public SetSignOutFlow(
		ctx: StateContext<SignInKioskStateModel>,
		{ payload }: { payload: SignOutPages[] },
	) {
		const { patchState } = ctx;
		patchState({ signOutFlow: payload });
	}
}
